import React from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../../components/layout/PublicLayout';
import PromoCodeModal from '../../components/PromoCodeModal';
import { PlanCard, PromoCodeSection, BillingControls } from '../../components/pricing';
import { usePricing } from '../../hooks/usePricing';
import { PRICING_TIERS } from '../../types/subscription';

const PricingPage: React.FC = () => {
  const {
    billingCycle,
    setBillingCycle,
    region,
    setRegion,
    isLoading,
    promoCode,
    setPromoCode,
    promoApplied,
    promoError,
    setPromoError,
    promoValidating,
    showPromoModal,
    currentUser,
    currentPlan,
    currencySymbol,
    earlyBirdActive,
    promoDiscount,
    getPrice,
    handleApplyPromo,
    handleRemovePromo,
    handlePromoApplied,
    handleSubscribe,
    handleManageSubscription,
    getButtonText,
    isCurrentPlan,
    openPromoModal,
    closePromoModal,
  } = usePricing();

  const renderPlanCards = () => {
    return PRICING_TIERS.map((tier) => {
      const basePrice = tier.pricing[region][billingCycle];
      const displayPrice = getPrice(basePrice);
      const monthlyPrice = billingCycle === 'annual' ? displayPrice / 12 : displayPrice;
      const isCurrent = isCurrentPlan(tier.id);
      const savings = basePrice > 0 && !promoApplied ? Math.round(promoDiscount * 100) : 0;

      return (
        <PlanCard
          key={tier.id}
          tier={tier}
          displayPrice={displayPrice}
          monthlyPrice={monthlyPrice}
          currencySymbol={currencySymbol}
          billingCycle={billingCycle}
          isCurrentPlan={!!isCurrent}
          isLoading={isLoading === tier.id}
          promoApplied={promoApplied}
          savings={savings}
          onSubscribe={handleSubscribe}
          getButtonText={getButtonText}
          theme="default"
          showTrialBadge={false}
        />
      );
    });
  };

  return (
    <PublicLayout>
      <div className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
              Simple, Transparent Pricing
            </h1>
            <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">
              Choose the plan that best fits your learning goals. Start free and upgrade anytime.
            </p>
          </div>

          {/* Promo Code Section — hidden when Early Bird is active (discount auto-applied) */}
          {!earlyBirdActive && (
            <PromoCodeSection
              promoCode={promoCode}
              setPromoCode={setPromoCode}
              promoApplied={promoApplied}
              promoError={promoError}
              setPromoError={setPromoError}
              promoValidating={promoValidating}
              onApply={handleApplyPromo}
              onRemove={handleRemovePromo}
              onRequestCode={openPromoModal}
            />
          )}

          {/* Billing Controls */}
          <BillingControls
            billingCycle={billingCycle}
            setBillingCycle={setBillingCycle}
            region={region}
            setRegion={setRegion}
          />

          {/* Payment Methods */}
          <div className="mt-8 flex justify-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">Pay securely with:</span>
                <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border-2 border-blue-500 shadow-sm">
                  <svg className="h-5 w-5 text-indigo-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                  </svg>
                  <span className="text-sm font-medium text-gray-900">Stripe</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400 hidden sm:inline">|</span>
                <span className="text-xs text-gray-500">Accepted cards:</span>
                {/* Visa */}
                <div className="bg-white px-2 py-1 rounded border border-gray-200">
                  <svg className="h-6 w-10" viewBox="0 0 780 500" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M293.2 348.73l33.36-195.76h53.34l-33.38 195.76H293.2zm246.11-191.54c-10.57-3.98-27.19-8.24-47.92-8.24-52.84 0-90.08 26.67-90.33 64.86-.5 28.18 26.45 43.89 46.68 53.28 20.72 9.63 27.69 15.77 27.69 24.39-.25 13.15-16.6 19.16-31.93 19.16-21.35 0-32.69-2.97-50.16-10.32l-6.87-3.11-7.49 43.89c12.46 5.47 35.53 10.2 59.47 10.45 56.19 0 92.68-26.3 93.18-67.11.25-22.38-14.02-39.39-44.83-53.4-18.62-9.12-30.06-15.15-30.06-24.39.25-8.24 9.69-16.86 30.68-16.86 17.48-.25 30.18 3.6 40.12 7.57l4.86 2.23 7.24-42.4h.37zm137.56-4.22h-41.24c-12.83 0-22.4 3.48-28.02 16.36l-79.4 179.65h56.07l11.07-29.3h68.27c1.62 7.2 6.49 29.3 6.49 29.3h49.53l-42.77-195.76v-.25zm-65.28 126.49c4.49-11.32 21.47-54.9 21.47-54.9-.25.5 4.36-11.45 7.11-18.89l3.6 17.01 12.46 56.78h-44.64zM247.61 152.97l-52.34 133.57-5.61-27.19c-9.69-31.19-39.75-65.11-73.56-82.09l47.67 170.84h56.57l84.14-195.13h-56.87z" fill="#1A1F71"/>
                    <path d="M146.92 152.97H62.69l-.75 4.22c67.15 16.24 111.54 55.38 130.04 102.42L173.11 169.7c-3.11-12.58-12.58-16.36-26.19-16.73z" fill="#F9A533"/>
                  </svg>
                </div>
                {/* Mastercard */}
                <div className="bg-white px-2 py-1 rounded border border-gray-200">
                  <svg className="h-6 w-10" viewBox="0 0 780 500" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="299" cy="250" r="150" fill="#EB001B"/>
                    <circle cx="481" cy="250" r="150" fill="#F79E1B"/>
                    <path d="M390 120.87c-42.66 33.85-70 86.14-70 144.63s27.34 110.78 70 144.63c42.66-33.85 70-86.14 70-144.63s-27.34-110.78-70-144.63z" fill="#FF5F00"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Plans Section */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
              Choose Your Plan
            </h2>
            <p className="text-gray-600 text-center mb-8">
              Pro includes everything: Pronunciation, IELTS prep, and Interview practice
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {renderPlanCards()}
            </div>
          </div>

          {/* Manage Subscription */}
          {currentUser && currentPlan !== 'free' && (
            <div className="mt-12 text-center">
              <button
                onClick={handleManageSubscription}
                disabled={isLoading === 'manage'}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                {isLoading === 'manage' ? 'Loading...' : 'Manage your subscription'}
              </button>
            </div>
          )}

          {/* FAQ Section */}
          <div className="mt-24">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I cancel anytime?</h3>
                <p className="text-gray-600">
                  Yes, you can cancel your subscription at any time. You'll continue to have access
                  until the end of your billing period.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">What payment methods do you accept?</h3>
                <p className="text-gray-600">
                  We accept all major credit cards through Stripe. PayPal support coming soon.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Is there a free trial?</h3>
                <p className="text-gray-600">
                  Yes! The free plan gives you 5 recordings per day to try out the basic features.
                  No credit card required.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I upgrade or downgrade?</h3>
                <p className="text-gray-600">
                  Absolutely! You can change your plan at any time. The change takes effect
                  immediately and we'll prorate your billing.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-24 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Still have questions?</h2>
            <p className="text-gray-600 mb-6">We're here to help. Contact our support team anytime.</p>
            <Link
              to="/contact"
              className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>

      {/* Promo Code Modal */}
      <PromoCodeModal
        isOpen={showPromoModal}
        onClose={closePromoModal}
        onPromoApplied={handlePromoApplied}
      />
    </PublicLayout>
  );
};

export default PricingPage;
